import { useState, useEffect, useCallback } from "react";
import api, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  ClipboardList,
  StickyNote,
  Link2,
  ListTodo,
  ExternalLink,
  Pin,
  PinOff,
  Check,
  Trophy,
  ChevronRight,
  RefreshCw,
  MapPin,
  Gamepad2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { linkify } from "@/lib/linkify";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${getApiUrl()}/api`;

/** Render markdown-style bold/italic */
function RichText({ text }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
    if (part.startsWith("*") && part.endsWith("*"))
      return <em key={i} className="italic">{part.slice(1, -1)}</em>;
    return <span key={i}>{linkify(part, `pl-${i}`)}</span>;
  });
}

export default function PlannerPanel({ gameId, refreshKey }) {
  const [achievementItems, setAchievementItems] = useState([]);
  const [gameItems, setGameItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedAchievements, setExpandedAchievements] = useState(new Set());
  const [expandedGameNotes, setExpandedGameNotes] = useState(true);

  const fetchPlannerItems = useCallback(async () => {
    if (!gameId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`${API}/games/${gameId}/planner-items`);
      // Support both old (array) and new ({achievementItems, gameItems}) format
      if (Array.isArray(data)) {
        setAchievementItems(data);
        setGameItems([]);
      } else {
        setAchievementItems(data.achievementItems || []);
        setGameItems(data.gameItems || []);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  useEffect(() => {
    fetchPlannerItems();
  }, [fetchPlannerItems, refreshKey]);

  const handleUnpinAchNote = async (noteId) => {
    try {
      await api.patch(`${API}/achievements/notes/${noteId}/toggle-planner`);
      toast.success("Removed from planner");
      fetchPlannerItems();
    } catch {
      toast.error("Failed to unpin");
    }
  };

  const handleUnpinGameNote = async (noteGameId, noteId) => {
    try {
      await api.patch(`${API}/games/${noteGameId}/notes/${noteId}/toggle-planner`);
      toast.success("Removed from planner");
      fetchPlannerItems();
    } catch {
      toast.error("Failed to unpin");
    }
  };

  const handleToggleChecklist = async (item, itemIndex) => {
    try {
      await api.patch(`${API}/achievements/${item.achievementId}/notes/${item.id}/checklist/${itemIndex}`);
      fetchPlannerItems();
    } catch {
      toast.error("Failed to toggle item");
    }
  };

  const handleToggleGameChecklist = async (noteGameId, noteId, itemIndex) => {
    try {
      await api.patch(`${API}/games/${noteGameId}/notes/${noteId}/checklist/${itemIndex}`);
      fetchPlannerItems();
    } catch {
      toast.error("Failed to toggle item");
    }
  };

  const handleToggleMapPin = async (noteGameId, noteId, pinId, checked) => {
    try {
      await api.patch(`${API}/games/${noteGameId}/notes/${noteId}/pins/${pinId}`, { checked: !checked });
      fetchPlannerItems();
    } catch {
      toast.error("Failed to toggle pin");
    }
  };

  // Group achievement items by achievement
  const grouped = achievementItems.reduce((acc, item) => {
    const key = item.achievementId;
    if (!acc[key]) {
      acc[key] = {
        achievementId: key,
        achievementName: item.achievementName || "Unknown",
        achievementImageUrl: item.achievementImageUrl,
        achievementUnlocked: item.achievementUnlocked,
        items: [],
      };
    }
    acc[key].items.push(item);
    return acc;
  }, {});

  const groups = Object.values(grouped);
  const totalItems = achievementItems.length + gameItems.length;

  if (loading && totalItems === 0) {
    return (
      <div className="text-center py-6 text-white/30 text-sm">
        Loading planner...
      </div>
    );
  }

  if (totalItems === 0) {
    return (
      <div className="text-center py-8 space-y-2" data-testid="planner-empty">
        <ClipboardList className="w-8 h-8 text-white/15 mx-auto" />
        <p className="text-white/30 text-sm">No planner items yet</p>
        <p className="text-white/20 text-xs">Pin notes, checklists, maps, or links to see them here</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="planner-panel">
      {/* Summary bar */}
      <div className="flex items-center justify-between">
        <span className="text-white/50 text-xs">
          {totalItems} item{totalItems !== 1 ? "s" : ""}
          {groups.length > 0 && ` across ${groups.length} achievement${groups.length !== 1 ? "s" : ""}`}
          {gameItems.length > 0 && ` + ${gameItems.length} game note${gameItems.length !== 1 ? "s" : ""}`}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[10px] text-white/40 hover:text-white/60"
          onClick={fetchPlannerItems}
          data-testid="planner-refresh-btn"
        >
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>

      {/* Game-level notes section */}
      {gameItems.length > 0 && (
        <div className="border border-amber-500/10 rounded-lg overflow-hidden" data-testid="planner-game-notes">
          <button
            onClick={() => setExpandedGameNotes(prev => !prev)}
            className="w-full flex items-center gap-2.5 p-2.5 bg-amber-500/5 hover:bg-amber-500/10 transition-colors text-left"
          >
            <div className="w-7 h-7 rounded bg-amber-500/10 flex items-center justify-center shrink-0">
              <Gamepad2 className="w-3.5 h-3.5 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium text-amber-400/80">Game Notes & Maps</span>
              <span className="text-[10px] text-white/40 block">{gameItems.length} pinned item{gameItems.length !== 1 ? "s" : ""}</span>
            </div>
            <ChevronRight className={`w-3.5 h-3.5 text-white/30 transition-transform ${expandedGameNotes ? "rotate-90" : ""}`} />
          </button>
          {expandedGameNotes && (
            <div className="p-2 space-y-1.5 bg-black/10">
              {gameItems.map(item => (
                <GameNoteItem
                  key={item.id}
                  item={item}
                  onUnpin={() => handleUnpinGameNote(item.gameId || gameId, item.id)}
                  onToggleChecklist={(idx) => handleToggleGameChecklist(item.gameId || gameId, item.id, idx)}
                  onToggleMapPin={(pinId, checked) => handleToggleMapPin(item.gameId || gameId, item.id, pinId, checked)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Achievement-grouped items */}
      {groups.map((group) => {
        const isExpanded = expandedAchievements.has(group.achievementId);
        const checklistTotal = group.items
          .filter((i) => i.noteType === "checklist")
          .reduce((sum, i) => sum + (i.checklistItems?.length || 0), 0);
        const checklistDone = group.items
          .filter((i) => i.noteType === "checklist")
          .reduce((sum, i) => sum + (i.checklistItems?.filter((ci) => ci.checked).length || 0), 0);

        return (
          <div key={group.achievementId} className="border border-white/5 rounded-lg overflow-hidden" data-testid={`planner-group-${group.achievementId}`}>
            {/* Achievement header */}
            <button
              onClick={() =>
                setExpandedAchievements((prev) => {
                  const next = new Set(prev);
                  next.has(group.achievementId) ? next.delete(group.achievementId) : next.add(group.achievementId);
                  return next;
                })
              }
              className="w-full flex items-center gap-2.5 p-2.5 bg-black/30 hover:bg-black/40 transition-colors text-left"
            >
              {/* Achievement image */}
              <div className="w-7 h-7 rounded bg-black/50 overflow-hidden shrink-0">
                {group.achievementImageUrl ? (
                  <img src={group.achievementImageUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Trophy className="w-3.5 h-3.5 text-white/20" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`text-xs font-medium truncate ${group.achievementUnlocked ? "text-green-400" : "text-white/80"}`}>
                    {group.achievementName}
                  </span>
                  {group.achievementUnlocked && <Check className="w-3 h-3 text-green-400 shrink-0" />}
                </div>
                <span className="text-[10px] text-white/40">
                  {group.items.length} pinned item{group.items.length !== 1 ? "s" : ""}
                  {checklistTotal > 0 && ` | ${checklistDone}/${checklistTotal} checked`}
                </span>
              </div>
              <ChevronRight className={`w-3.5 h-3.5 text-white/30 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
            </button>

            {/* Items */}
            {isExpanded && (
              <div className="p-2 space-y-1.5 bg-black/10">
                {group.items.map((item) => (
                  <PlannerItem
                    key={item.id}
                    item={item}
                    onUnpin={() => handleUnpinAchNote(item.id)}
                    onToggleChecklist={(idx) => handleToggleChecklist(item, idx)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PlannerItem({ item, onUnpin, onToggleChecklist }) {
  const typeIcon =
    item.noteType === "link" ? <Link2 className="w-3 h-3 text-blue-400 shrink-0" /> :
    item.noteType === "checklist" ? <ListTodo className="w-3 h-3 text-emerald-400 shrink-0" /> :
    item.noteType === "map" ? <MapPin className="w-3 h-3 text-amber-400 shrink-0" /> :
    <StickyNote className="w-3 h-3 text-amber-400 shrink-0" />;

  return (
    <div className="bg-black/20 rounded p-2 border border-white/5 group/planner" data-testid={`planner-item-${item.id}`}>
      <div className="flex items-start gap-1.5">
        {typeIcon}
        <div className="flex-1 min-w-0">
          {item.title && (
            <span className="text-white/80 text-xs font-medium block">{item.title}</span>
          )}

          {item.noteType === "link" ? (
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1 mt-0.5">
              <ExternalLink className="w-3 h-3 shrink-0" />
              <span className="truncate">{item.text}</span>
            </a>
          ) : item.noteType === "checklist" ? (
            <div className="space-y-1 mt-1">
              {(item.checklistItems || []).map((ci, i) => (
                <label key={i} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <Checkbox
                    checked={ci.checked}
                    onCheckedChange={() => onToggleChecklist(i)}
                    className="w-3 h-3"
                  />
                  <span className={ci.checked ? "line-through text-white/30" : "text-white/70"}>{ci.text}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-white/60 text-xs mt-0.5 whitespace-pre-wrap break-words">
              <RichText text={item.text} />
            </div>
          )}
        </div>
        <button
          onClick={onUnpin}
          className="text-cyan-400/40 hover:text-cyan-400 p-0.5 opacity-0 group-hover/planner:opacity-100 transition-opacity shrink-0"
          title="Remove from planner"
        >
          <PinOff className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function GameNoteItem({ item, onUnpin, onToggleChecklist, onToggleMapPin }) {
  const typeIcon =
    item.noteType === "map" ? <MapPin className="w-3 h-3 text-amber-400 shrink-0" /> :
    item.noteType === "checklist" ? <ListTodo className="w-3 h-3 text-emerald-400 shrink-0" /> :
    <StickyNote className="w-3 h-3 text-amber-400 shrink-0" />;

  return (
    <div className="bg-black/20 rounded p-2 border border-white/5 group/planner" data-testid={`planner-game-item-${item.id}`}>
      <div className="flex items-start gap-1.5">
        {typeIcon}
        <div className="flex-1 min-w-0">
          {(item.title || item.text) && (
            <span className="text-white/80 text-xs font-medium block">{item.title || item.text}</span>
          )}

          {item.noteType === "map" ? (
            <div className="mt-1 space-y-1">
              {item.mapImageUrl && (
                <div className="w-full h-20 rounded overflow-hidden border border-white/10">
                  <img src={item.mapImageUrl} alt="Map" className="w-full h-full object-cover" />
                </div>
              )}
              {(item.pins || []).map((pin) => (
                <label
                  key={pin.id}
                  className="flex items-center gap-1.5 text-xs cursor-pointer"
                  onClick={() => onToggleMapPin(pin.id, pin.checked)}
                >
                  <Checkbox checked={pin.checked} className="w-3 h-3" />
                  <div
                    className="w-3 h-3 rounded-full shrink-0 flex items-center justify-center"
                    style={{ backgroundColor: pin.color || "#f59e0b" }}
                  >
                    <span className="text-[7px] font-bold text-black">{(item.pins || []).indexOf(pin) + 1}</span>
                  </div>
                  <span className={pin.checked ? "line-through text-white/30" : "text-white/70"}>
                    {pin.label}
                  </span>
                </label>
              ))}
            </div>
          ) : item.noteType === "checklist" ? (
            <div className="space-y-1 mt-1">
              {(item.checklistItems || []).map((ci, i) => (
                <label key={i} className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <Checkbox
                    checked={ci.checked}
                    onCheckedChange={() => onToggleChecklist(i)}
                    className="w-3 h-3"
                  />
                  <span className={ci.checked ? "line-through text-white/30" : "text-white/70"}>{ci.text}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="text-white/60 text-xs mt-0.5 whitespace-pre-wrap break-words">
              <RichText text={item.text} />
            </div>
          )}
        </div>
        <button
          onClick={onUnpin}
          className="text-cyan-400/40 hover:text-cyan-400 p-0.5 opacity-0 group-hover/planner:opacity-100 transition-opacity shrink-0"
          title="Remove from planner"
        >
          <PinOff className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
